<script setup lang="ts" generic="TSliderMode extends SliderMode">
import { createSlider } from "@sit-onyx/headless";
import { iconMinusSmall, iconPlusSmall } from "@sit-onyx/icons";
import { computed, ref, toRef, toRefs, type HTMLAttributes } from "vue";
import { useFormElementError } from "../../composables/useFormElementError.js";
import { SKELETON_INJECTED_SYMBOL } from "../../composables/useSkeletonState.js";
import { useVModel } from "../../composables/useVModel.js";
import { injectI18n } from "../../i18n/index.js";
import { mergeVueProps, useRootAttrs } from "../../utils/attrs.js";
import { FORM_INJECTED_SYMBOL, useFormContext } from "../OnyxForm/OnyxForm.core.js";
import OnyxFormElementV2 from "../OnyxFormElementV2/OnyxFormElementV2.vue";
import { useLegacyFormElementProps } from "../OnyxFormElementV2/useLegacyFormElementProps.js";
import OnyxIconButton from "../OnyxIconButton/OnyxIconButton.vue";
import type { OnyxIconButtonProps } from "../OnyxIconButton/types.js";
import OnyxStepper from "../OnyxStepper/OnyxStepper.vue";
import type { OnyxStepperProps } from "../OnyxStepper/types.js";
import OnyxTooltip from "../OnyxTooltip/OnyxTooltip.vue";
import OnyxVisuallyHidden from "../OnyxVisuallyHidden/OnyxVisuallyHidden.vue";
import type {
  NormalizedSliderMark,
  OnyxSliderProps,
  SliderMark,
  SliderMode,
  SliderValue,
} from "./types.js";

type Props = OnyxSliderProps<TSliderMode>;

const props = withDefaults(defineProps<Props>(), {
  min: 0,
  max: 100,
  step: 1,
  skeleton: SKELETON_INJECTED_SYMBOL,
  disabled: FORM_INJECTED_SYMBOL,
  showError: FORM_INJECTED_SYMBOL,
  requiredMarker: FORM_INJECTED_SYMBOL,
  reserveMessageSpace: FORM_INJECTED_SYMBOL,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- false positive
  mode: () => "single" as TSliderMode,
});

const emit = defineEmits<{
  /**
   * Emitted when the current value changes.
   */
  "update:modelValue": [value: SliderValue<TSliderMode>];
  /**
   * Emitted when the validity state of the slider changes.
   */
  validityChange: [validity: ValidityState];
}>();

const slots = defineSlots<{
  /**
   * Optional slot to pass in custom content for a mark label (below the slider track).
   * Useful for showing icons etc.
   */
  mark?(props: SliderMark): unknown;
}>();

const modelValue = useVModel<Props, "modelValue", SliderValue<TSliderMode>>({
  props,
  emit,
  key: "modelValue",
});

const { t } = injectI18n();
const { vCustomValidity, errorMessages } = useFormElementError({ props, emit });

const { disabled } = useFormContext(props);

const { min, max, step } = toRefs(props);

const labelString = computed(() => {
  if (typeof props.label === "object" && props.label !== null) return props.label.label;
  return props.label;
});

const {
  elements: { root, thumbInput },
  state: { normalizedValue, track },
  internals: { updateValue, updateValueByStep, getValueInPercentage },
} = createSlider({
  value: modelValue,
  min,
  max,
  step,
  label: labelString,
  disabled,
  shiftStep: toRef(() => props.shiftStep),
  onChange: (newValue) => {
    modelValue.value = newValue;
    wasTouched.value = true;
  },
});

const marks = computed<NormalizedSliderMark[]>(() => {
  const _marks = props.marks;
  if (!_marks) return [];

  let normalizedMarks: SliderMark[];
  if (_marks === true) {
    // auto-generate marks based on step
    const markCount = Math.floor((max.value - min.value) / step.value) + 1;
    normalizedMarks = Array.from({ length: markCount }, (_, index) => ({
      value: min.value + step.value * index,
    }));
  } else {
    // normalize user provided marks
    normalizedMarks = _marks
      .map((mark) => {
        if (typeof mark === "number") return { value: mark };
        return mark;
      })
      .sort((a, b) => a.value - b.value);
  }

  return normalizedMarks.map((mark) => {
    return {
      ...mark,
      percentage: getValueInPercentage.value(mark.value),
    };
  });
});

const hasMarkLabels = computed(() => marks.value.some((mark) => !!mark.label));

/**
 * Used to detect user interaction to simulate the behavior of :user-invalid for the native input
 * because the native browser :user-invalid does not trigger when the value is changed e.g. via
 * slider click.
 */
const wasTouched = ref(false);

const sharedIconButtonProps = computed(() => {
  return {
    color: "neutral",
    // tabindex is set here because the icon buttons are only relevant for mouse users
    // so they should be ignored by keyboard / screen readers because the slider itself is already (keyboard) accessible.
    tabindex: -1,
    onMousedown: (ev) => {
      // needed to not loose the tooltip focus when holding down the mouse during a click
      ev.preventDefault();
    },
  } satisfies Partial<OnyxIconButtonProps> & HTMLAttributes;
});

const { formElementV2Props } = useLegacyFormElementProps({ props, errorMessages });

const sharedStepperProps = computed(() => {
  return {
    class: "onyx-slider__control",
    stepSize: props.step,
    min: props.min,
    max: props.max,
    disabled: disabled.value,
    hideLabel: true,
    hideButtons: true,
  } satisfies Partial<OnyxStepperProps> & HTMLAttributes;
});
const { rootAttrs, restAttrs } = useRootAttrs();
</script>

<template>
  <OnyxFormElementV2 v-bind="mergeVueProps(formElementV2Props, rootAttrs)" unstyled>
    <template #default="inputProps">
      <div :class="['onyx-slider', 'onyx-slider__container']">
        <!-- left controls -->
        <span v-if="props.control === 'value'" class="onyx-slider__control">{{ props.min }}</span>

        <OnyxIconButton
          v-else-if="props.control === 'icon' && props.mode === 'single'"
          v-bind="sharedIconButtonProps"
          :label="t('slider.decreaseValueBy', { n: props.step })"
          :icon="iconMinusSmall"
          :disabled="disabled || normalizedValue[0] <= props.min"
          @click="updateValueByStep('decrease')"
        />

        <OnyxStepper
          v-else-if="props.control === 'input' && props.mode === 'range'"
          v-bind="sharedStepperProps"
          :label="t('slider.changeStartValue')"
          :model-value="normalizedValue[0]"
          @update:model-value="$event != undefined && updateValue($event, 0)"
        />

        <!-- main slider -->
        <span class="onyx-slider__root" v-bind="root">
          <span
            class="onyx-slider__rail"
            :style="{
              '--track-start-percentage': `${track.startPercentage}%`,
              '--track-end-percentage': `${100 - track.endPercentage}%`,
            }"
          ></span>

          <template v-for="markItem in marks" :key="markItem.value">
            <span
              class="onyx-slider__mark"
              :style="{
                left: `${getValueInPercentage(markItem.value)}%`,
              }"
            ></span>
            <span
              v-if="markItem.label || slots.mark"
              class="onyx-slider__mark-label"
              :style="{
                left: `${getValueInPercentage(markItem.value)}%`,
              }"
            >
              <slot name="mark" v-bind="markItem">
                {{ markItem.label }}
              </slot>
            </span>
          </template>

          <OnyxTooltip
            v-for="(value, index) in normalizedValue"
            :key="index"
            :style="{ left: `${getValueInPercentage(value)}%` }"
            class="onyx-slider__thumb"
            :open="props.tooltip?.hidden ? false : undefined"
            :text="props.tooltip?.formatter?.(value, index) ?? String(value)"
            :position="hasMarkLabels ? 'top' : 'bottom'"
          >
            <template #default="{ trigger }">
              <OnyxVisuallyHidden
                v-bind="mergeVueProps(thumbInput({ value, index }), trigger, restAttrs, inputProps)"
                is="input"
                v-custom-validity
                class="onyx-slider__native"
                :tabindex="props.control === 'input' ? -1 : undefined"
                :disabled="disabled"
                :aria-label="props.label"
                :autofocus="props.autofocus && index === 0"
              />
            </template>
          </OnyxTooltip>
        </span>

        <!-- right controls -->
        <span v-if="props.control === 'value'" class="onyx-slider__control">{{ props.max }}</span>

        <OnyxIconButton
          v-else-if="props.control === 'icon' && props.mode === 'single'"
          v-bind="sharedIconButtonProps"
          :label="t('slider.increaseValueBy', { n: props.step })"
          :icon="iconPlusSmall"
          :disabled="disabled || normalizedValue[0] >= props.max"
          @click="updateValueByStep('increase')"
        />

        <OnyxStepper
          v-else-if="props.control === 'input'"
          v-bind="sharedStepperProps"
          :label="t(`slider.${props.mode === 'range' ? 'changeEndValue' : 'changeValue'}`)"
          :model-value="normalizedValue[1] ?? normalizedValue[0]"
          @update:model-value="
            (event) => event != undefined && updateValue(event, props.mode === 'range' ? 1 : 0)
          "
        />
      </div>
    </template>
  </OnyxFormElementV2>
</template>

<style lang="scss">
@use "../../styles/mixins/layers.scss";
@use "../../styles/mixins/input.scss";

.onyx-slider {
  @include layers.component() {
    // Colors
    --onyx-slider-rail-background: var(--onyx-color-base-neutral-200);
    --onyx-slider-track-background: var(--onyx-color-base-neutral-600);
    --onyx-slider-thumb-background: var(--onyx-color-base-neutral-600);
    --onyx-slider-thumb-border-color: var(--onyx-color-base-neutral-800);
    --onyx-slider-mark-background: var(--onyx-color-base-neutral-400);
    --onyx-slider-mark-color: var(--onyx-color-text-icons-neutral-medium);

    // Spacings
    --onyx-slider-padding-vertical: var(--onyx-density-xs);
    --onyx-slider-root-padding: var(--onyx-density-sm);
    --onyx-slider-mark-label-offset: var(--onyx-density-xl);
    --onyx-slider-thumb-size: 1.25rem;

    // Interactive state colors
    --onyx-slider-rail-background-interactive: var(--onyx-color-base-neutral-200);
    --onyx-slider-track-background-interactive: var(--onyx-color-base-primary-500);
    --onyx-slider-thumb-background-interactive: var(--onyx-color-base-primary-500);
    --onyx-slider-thumb-border-color-interactive: var(--onyx-color-base-primary-500);

    // Disabled state colors
    --onyx-slider-background-disabled: var(--onyx-color-base-neutral-300);
    --onyx-slider-mark-color-disabled: var(--onyx-color-text-icons-neutral-soft);

    --onyx-slider-offset: calc(0.375rem + 2 * var(--onyx-1px-in-rem));
  }
}

.onyx-slider {
  @include layers.component() {
    @include input.define-shared-styles(
      $base-selector: ".onyx-slider",
      $vertical-padding: var(--onyx-slider-padding-vertical)
    );

    $track-z-index: 1;

    &__container {
      display: flex;
      width: 100%;
      align-items: center;
      gap: var(--onyx-density-sm);
      color: var(--onyx-color-text-icons-neutral-intense);

      &:has(.onyx-slider__mark-label) {
        padding-bottom: var(--onyx-slider-mark-label-offset);
      }
    }

    &__root {
      position: relative;
      display: inline-flex;
      align-items: center;
      width: 100%;
      height: 0.5rem;
      padding: var(--onyx-slider-root-padding) 0;
      user-select: none;
      margin-inline: calc(0.5 * var(--onyx-slider-offset));
      border-radius: var(--onyx-radius-sm);
    }

    &__control {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      min-width: 2.5rem;
      height: 2.5rem;
      font-size: var(--onyx-font-size-md);
      font-family: var(--onyx-font-family-paragraph);
      line-height: var(--onyx-font-line-height-sm);
      user-select: none;
    }

    &__rail {
      width: 100%;
      height: inherit;

      &::before,
      &::after {
        position: absolute;
        display: block;
        height: inherit;
        content: "";
        border-radius: var(--onyx-radius-sm);
        margin-left: calc(-0.5 * var(--onyx-slider-offset));
      }

      &::before {
        width: calc(100% + var(--onyx-slider-offset));
        background-color: var(--onyx-slider-rail-background);
      }

      &::after {
        left: var(--track-start-percentage);
        right: var(--track-end-percentage);
        background-color: var(--onyx-slider-track-background);
        z-index: $track-z-index;
      }
    }

    &__native {
      // give the native input the same size as the parent
      width: inherit;
      height: inherit;
      border: inherit;
    }

    &__thumb {
      position: absolute;
      width: var(--onyx-slider-thumb-size);
      height: var(--onyx-slider-thumb-size);
      margin-left: calc(-1 * var(--onyx-slider-thumb-size) / 2);
      border-radius: var(--onyx-radius-full);
      background-color: var(--onyx-slider-thumb-background);
      border: 0.25rem solid var(--onyx-slider-thumb-border-color);
      display: grid;
      place-items: center;
      z-index: $track-z-index + 1;

      .onyx-tooltip {
        --offset: 0.25rem;
        pointer-events: none;
      }

      &:has(.onyx-slider__native:enabled) {
        &:focus-within {
          outline: var(--onyx-outline-width) solid var(--onyx-color-component-focus-primary);
        }
      }
    }

    &__mark {
      position: absolute;
      width: 0.375rem;
      height: 0.375rem;
      border-radius: var(--onyx-radius-full);
      background-color: var(--onyx-slider-mark-background);
      transform: translateX(-50%);
    }

    &__mark-label {
      color: var(--onyx-slider-mark-color);
      font-size: var(--onyx-font-size-sm);
      font-family: var(--onyx-font-family-paragraph);
      line-height: var(--onyx-font-line-height-sm);
      position: absolute;
      white-space: nowrap;
      top: var(--onyx-slider-mark-label-offset);
      display: flex;
      transform: translateX(-50%);
    }

    &:has(&__native:enabled) {
      .onyx-slider__root {
        cursor: pointer;
      }

      &:has(.onyx-slider__root:hover),
      &:has(.onyx-slider__root:focus-within) {
        --onyx-slider-rail-background: var(--onyx-slider-rail-background-interactive);
        --onyx-slider-track-background: var(--onyx-slider-track-background-interactive);
        --onyx-slider-thumb-background: var(--onyx-slider-thumb-background-interactive);
        --onyx-slider-thumb-border-color: var(--onyx-slider-thumb-border-color-interactive);
      }
    }

    &:has(&__native:disabled) {
      --onyx-slider-track-background: var(--onyx-slider-background-disabled);
      --onyx-slider-thumb-background: var(--onyx-slider-background-disabled);
      --onyx-slider-thumb-border-color: var(--onyx-slider-background-disabled);
      --onyx-slider-mark-color: var(--onyx-slider-mark-color-disabled);
    }
  }
}
</style>
